import {
  ActionIcon,
  Button,
  Container,
  Divider,
  Group,
  Image,
  List,
  NumberInput,
  ThemeIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { motion } from "framer-motion";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiMinusSm, HiPlusSm } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  addArticle,
  addPackageTicketBatch,
  addToCartTracking,
} from "../../../common/sale";
import { STATE_CULTURE, STATE_CURRENCY } from "../../../common/state";
import { translateProductGroup } from "../../../common/util";
import { Context, SUSPEND_OPEN_CART } from "../../../context";
import Description from "../../common/Description";
import { SaleContext } from "../../common/saleProvider";
import slugify from "../../common/slugify";
import { formatDisplayPrice } from "../../ProductList/util";
import { getSaleData } from "../util";
import classes from "./GroupProduct.module.css";
import ProductSelector from "./ProductSelector";
import "./styles.css";

const GroupProduct = ({ product, productDescription }) => {
  const { config, culture, language, currency } = useSelector((state) => ({
    config: state.config,
    culture: state.cookies[STATE_CULTURE],
    language: state.language,
    currency: state.cookies[STATE_CURRENCY],
  }));
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handlers = useRef();
  const quantityChangeRef = useRef();
  const rdxDispatch = useDispatch();
  const { dispatch } = useContext(Context);
  const saleContext = useContext(SaleContext);
  const { sale } = saleContext.state;
  const location = useLocation();
  const fileName = `${slugify(product.name)}.${culture}.md`;
  const isMobile = useMediaQuery("(max-width: 768px)");

  const productData = useMemo(() => {
    if (selected?.[0]?.isPackage) {
      return new Array(quantity).fill({
        id: selected?.[0]?.plu,
        plu: selected?.[0]?.plu,
        name: selected?.[0]?.name,
        price: selected?.[0]?.price?.amountInclVat,
        quantity: 1,
      });
    } else {
      return [
        {
          id: selected?.[0]?.plu,
          plu: selected?.[0]?.plu,
          name: selected?.[0]?.name,
          price: selected?.[0]?.price?.amountInclVat,
          quantity: quantity,
        },
      ];
    }
  }, [selected, quantity]);

  const handleAddPackageTicket = async (callback) => {
    dispatch({
      type: SUSPEND_OPEN_CART,
      payload: true,
    });
    try {
      if (productData?.length > 0) {
        await new Promise((resolve, reject) => {
          rdxDispatch(
            addPackageTicketBatch(productData, (status, msg) => {
              if (status === 200) {
                addToCartTracking(currency, productData);
              } else {
                console.error("Error in package ticket:", msg);
              }
            })
          )
            .then(resolve)
            .catch(reject);
        });
      }
    } catch (error) {
      console.error("Error during add operation:", error);
    } finally {
      callback();
    }
  };

  const handleAdd = () => {
    addToCartTracking(currency, productData);
    announceQuantityChange(quantity);
    setIsLoading(true);
    const callback = (status, msg) => {
      if (status === 200) {
        setQuantity(1);
        setAddedToCart(true);
        setTimeout(() => {
          setAddedToCart(null);
        }, 5000);
      }
      if (status !== 200) {
        setQuantity(1);
        setAddedToCart(false);
        setTimeout(() => {
          setAddedToCart(null);
        }, 5000);
      }
      setIsLoading(false);
      dispatch({
        type: SUSPEND_OPEN_CART,
        payload: false,
      });
    };
    let article;
    let values;
    let photo;
    let overrideDate;
    const saleData = product.articles
      ? getSaleData(selected[0], article, values, photo, quantity, overrideDate)
      : {
          plu: selected.plu,
          quantity: quantity,
          priceInclVat: selected.amountInclVat,
        };
    if (selected?.[0]?.isPackage) {
      handleAddPackageTicket(callback);
    } else {
      rdxDispatch(addArticle(saleData, callback));
    }
  };

  useEffect(() => {
    setSelected(null);
    setQuantity(1);
  }, [location]);

  const uri = config?.apiUriOverride?.startsWith("http")
    ? config.apiUriOverride
    : config.apiUri;
  const img = `${uri}/articleimage/${selected?.[0]?.imageFilename}`;
  const regex = /\D/g;
  const reduce =
    product.articles &&
    product.articles.reduce((acc, curr) => {
      acc.push(curr.priceInclVat);
      return acc;
    }, []);

  const reduceChooseValue =
    product.openPriceValues &&
    product.openPriceValues.reduce((acc, curr) => {
      acc.push(curr);
      return acc;
    }, []);

  const sortPrice = (prices) => {
    if (prices && typeof prices?.[0] === typeof 0) {
      const sortOpenPrice = prices.sort((a, b) => a - b);
      return sortOpenPrice;
    } else if (prices && typeof prices?.[0] === typeof "") {
      const sortStringValue = prices.sort(
        (a, b) => a.replace(regex, "") - b.replace(regex, "")
      );
      return sortStringValue;
    }
  };

  const productGroupTranslation = translateProductGroup(
    product.name,
    language,
    "ProductGroups"
  );

  const name = useMemo(() => {
    const getTranslation = product?.translation?.filter(
      (el) => el?.culture === culture
    )?.[0];
    const translation = selected?.[0]?.translation?.find(
      (lang) => lang.culture === culture
    );
    if (translation) {
      return translation?.name;
    } else if (selected?.name) {
      return selected?.name;
    } else if (getTranslation) {
      return getTranslation?.name;
    } else if (product?.name) {
      return productGroupTranslation;
    }
  }, [product, selected, culture]);

  const description = useMemo(() => {
    const getTranslation = product?.translation?.filter(
      (el) => el?.culture === culture
    )?.[0];
    const translation = selected?.[0]?.translation?.find(
      (lang) => lang.culture === culture
    );
    if (translation) {
      return translation?.description;
    } else if (getTranslation?.description) {
      return getTranslation?.description;
    } else if (productDescription) {
      return productDescription;
    }
  }, [product, selected, culture, productDescription]);

  const price = useCallback(() => {
    if (product?.priceInclVat > 0) {
      return product?.priceInclVat;
    }

    if (product?.openPriceValues && !reduceChooseValue) {
      return `${language.FromCost} ${formatDisplayPrice(
        product?.openPriceValues?.[0],
        currency,
        0
      )}`;
    }

    if (selected?.[0]?.priceInclVat) {
      return selected?.[0].priceInclVat;
    }

    if (reduce) {
      return `${sortPrice(reduce)?.[0]}–${
        sortPrice(reduce)?.[sortPrice(reduce).length - 1]
      }`;
    }

    if (reduceChooseValue) {
      return `${formatDisplayPrice(
        sortPrice(reduceChooseValue)?.[0],
        currency,
        0
      )} – ${formatDisplayPrice(
        sortPrice(reduceChooseValue)?.[sortPrice(reduceChooseValue).length - 1],
        currency,
        0
      )}`;
    }
  }, [product, selected, reduceChooseValue, reduce]);

  const disabledIncrement = useMemo(() => {
    if (sale) {
      const qty = sale.items.filter(
        (el) => el.article.plu === selected?.[0]?.plu
      )?.[0]?.quantity;
      const calc = selected?.[0]?.inStock - (qty ? qty : 0);
      if (quantity >= calc) {
        return true;
      }
    }
  }, [sale, selected, quantity]);

  const disabledAdd = useMemo(() => {
    if (sale) {
      const qty = sale.items.filter(
        (el) => el.article.plu === selected?.[0]?.plu
      )?.[0]?.quantity;
      const calc = selected?.[0]?.inStock - (qty ? qty : 0);
      if (quantity > calc) {
        return true;
      }
    }
  }, [sale, selected, quantity]);

  const isAddedToCart = sale?.items.filter(
    (el) => el?.article?.plu === selected?.[0]?.plu
  );

  const announceQuantityChange = (newQuantity) => {
    quantityChangeRef.current.innerText = `${language.Quantity} ${newQuantity}`;
  };

  const increment = () => {
    handlers.current.increment();
    announceQuantityChange(quantity + 1);
  };

  const decrement = () => {
    handlers.current.decrement();
    announceQuantityChange(quantity - 1);
  };

  if (!product) return null;
  return (
    <Container className={classes.containerWrapper}>
      <div className={classes.wrapper}>
        <div>
          <motion.div
            className={classes.imageWrapper}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Image
              src={`${
                selected?.[0]?.imageFilename ? img : product?.images?.[0]
              }`}
              radius={"sm"}
              className={classes.image}
              fallbackSrc={product?.images?.[0]}
              h={"auto"}
              fit="cover"
              alt={`${language.ProductAltLabel} ${name}`}
            />
          </motion.div>
        </div>
        <div className={classes.container}>
          <div className={classes.titleWrapper}>
            <h1 className="productTitle" style={{ fontSize: "1.5rem" }}>
              {name}
            </h1>
          </div>
          <div className={`${classes.stockWrapper} stockWrapper`}>
            <div>
              {selected && selected?.[0]?.isProduct ? (
                <List
                  size="sm"
                  center
                  className={`${classes.list} stockIndicatorGroup`}
                >
                  <List.Item
                    style={{ display: "flex", justifySelf: "end" }}
                    icon={
                      <ThemeIcon
                        color={
                          selected?.[0].inStock > 0
                            ? "var(--button-add-to-cart-success-bg-color)"
                            : "#FA5252"
                        }
                        size={19}
                        radius="xl"
                      >
                        <div />
                      </ThemeIcon>
                    }
                  >
                    <p
                      style={{ paddingTop: "0.1rem", margin: "0 0 0 -0.2rem" }}
                    >
                      {selected?.[0].inStock > 0
                        ? language.In_Stock
                        : language.Not_In_Stock}
                    </p>
                  </List.Item>
                </List>
              ) : (
                <div />
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "end" }}>
              <span
                className={`${classes.price} product-price`}
                component="span"
              >
                {price()}
              </span>
            </div>
          </div>
          <ProductSelector product={product} setSelected={setSelected} />
          <div className={classes.actionGrid}>
            <div
              className={classes.hideLabel}
              role="region"
              aria-live="assertive"
              ref={quantityChangeRef}
            />
            <Group
              gap={5}
              styles={{ height: "42px" }}
              className={classes.actionWrapper}
            >
              <ActionIcon
                className={`${classes.actionButton} addToCart`}
                size={35}
                color="gray"
                radius="50"
                variant="filled"
                onClick={() => decrement()}
                disabled={
                  (!selected && true) ||
                  quantity === 1 ||
                  product.isGiftCertificate ||
                  product.name.includes("Presentkort")
                }
                aria-label={language.DecreaseQuantity}
              >
                <div style={{ fontSize: "1.5rem", height: "24px" }}>
                  <HiMinusSm />
                </div>
              </ActionIcon>
              <NumberInput
                hideControls
                value={quantity}
                aria-label={`${language.Quantity} ${quantity}`}
                onChange={(val) => setQuantity(val)}
                disabled={
                  (!selected && true) ||
                  product.isGiftCertificate ||
                  product.name.includes("Presentkort")
                }
                handlersRef={handlers}
                max={selected?.[0]?.inStock ? selected?.[0].inStock : 99999}
                min={1}
                step={1}
                styles={{
                  input: {
                    backgroundColor: "transparent",
                    border: "none",
                    width: 35,
                    textAlign: "center",
                    height: 35,
                    fontWeight: "600",
                    fontSize: "1.25rem",
                    padding: 0,
                  },
                }}
              />
              <ActionIcon
                aria-label={language.AddQuantity}
                className={`${classes.actionButton} addToCart`}
                size={35}
                color="gray"
                radius="50"
                disabled={
                  (disabledIncrement
                    ? disabledIncrement
                    : selected?.[0]?.inStock
                    ? selected?.[0]?.inStock === quantity
                    : !selected && true) ||
                  product.isGiftCertificate ||
                  product.name.includes("Presentkort")
                }
                variant="filled"
                onClick={() => increment()}
              >
                <div style={{ fontSize: "1.5rem", height: "24px" }}>
                  <HiPlusSm />
                </div>
              </ActionIcon>
            </Group>
            <div
              className={classes.alertWrapper}
              role="region"
              aria-live="assertive"
            >
              <Button
                variant="filled"
                size={isMobile ? "sm" : "md"}
                className="regularButton add_to_cart_product"
                classNames={{
                  root: addedToCart && classes.addedToCart,
                  label: "add_to_cart_product",
                  inner: "add_to_cart_product",
                }}
                onClick={handleAdd}
                disabled={
                  quantity === 0 || !selected || disabledAdd || isLoading
                }
                color="var(--button-primary-bg-color)"
                loading={isLoading}
                style={{
                  width: "100%",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {disabledAdd && isAddedToCart?.length > 0
                  ? language.MaxInCart
                  : disabledAdd && language.Not_In_Stock}
                {!disabledAdd && language.AddToCartBtn}
              </Button>
            </div>
          </div>

          <Divider my="lg" />
          <h2 className={`${classes.descriptionTitle} descriptionTitle`}>
            {language.Description}
          </h2>
          <div className={`${classes.descriptionBody} productDescription`}>
            <Description text={description} fileName={fileName} />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default GroupProduct;
